// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import React, { useMemo } from 'react';
import { NavLink } from 'react-router';
import { useSelector, shallowEqual } from 'react-redux';

import { useMediaQuery } from '../../../../hooks/useMediaQuery';

import { Icons } from '../../../../../web-utils/src/UI/Component/React/V1/Icons/Icons';

import { UserNotifications } from '../UserNotifications/UserNotifications';
import { UserNavigation } from '../UserNavigation/UserNavigation';
import { CameraNavigation } from '../CameraNavigation/CameraNavigation';
import { StudioNavigation } from '../StudioNavigation/StudioNavigation';
import { CompanyNavigation } from '../CompanyNavigation/CompanyNavigation';

import styles from '../Navigation.module.css';

import button from '../../../UI/Button/Button.module.css';

const getNavigationLinks = ({ isLocalUser, isAdmin }) => {
  const isCloudUser = !isLocalUser;

  return {
    mainLinks: [
      {
        id: 'dashboard',
        to: '/',
        iconType: isLocalUser ? 'card' : 'screen',
        label: 'Dashboard',
        isAllowed: true,
      },
      {
        id: 'library',
        to: '/library',
        iconType: 'library',
        label: 'Library',
        isAllowed: isCloudUser,
      },
      {
        id: 'nodes',
        to: '/nodes',
        iconType: 'card',
        label: 'Equipment',
        isAllowed: isCloudUser,
      },
    ].filter(l => l.isAllowed),

    adminLinks: [
      {
        id: 'account',
        to: '/admin/account',
        iconType: 'user',
        label: 'Account',
        isAllowed: isLocalUser,
      },
      {
        id: 'users',
        to: '/admin/users',
        iconType: 'users',
        label: 'Users',
        isAllowed: isLocalUser && isAdmin,
      },
      {
        id: 'companies',
        to: '/admin/companies',
        iconType: 'devices',
        label: 'Companies',
        isAllowed: isCloudUser,
      },
      {
        id: 'studios',
        to: '/admin/studios',
        iconType: 'settings',
        label: 'Studios',
        isAllowed: isCloudUser,
      },
    ].filter(l => l.isAllowed),
  };
};

export const NavigationList = ({ isLocalUser, isAdmin }) => {
  const { companies, company } = useSelector(state => {
    const c = state.companies;
    return {
      companies: c.companies,
      company: c.company,
    };
  }, shallowEqual);

  const isMobile = useMediaQuery('(max-width: 768px)');

  const { mainLinks, adminLinks } = useMemo(
    () => getNavigationLinks({ isLocalUser, isAdmin }),
    [isLocalUser, isAdmin]
  );

  return (
    <>
      {!!companies?.length && (
        <>
          {mainLinks.map(({ id, to, iconType, label }) => (
            <NavLink
              key={id}
              to={to}
              className={({ isActive }) =>
                `${styles.link} ${button.btnNavigation} ${isActive ? styles.linkactive : ''}`
              }
              end
            >
              <Icons iconType={iconType} fill='var(--color-light)' stroke='var(--color-light)' />
              {isMobile && label}
            </NavLink>
          ))}
          {!isLocalUser && (
            <>
              <CompanyNavigation companies={companies} company={company} />
              <StudioNavigation />
            </>
          )}
          <CameraNavigation />
        </>
      )}
      <UserNotifications />
      <UserNavigation adminLinks={adminLinks} isLocalUser={isLocalUser} />
    </>
  );
};
